/**
 * Halcy's ring-and-wave symbol, in a 100 × 100 viewBox.
 *
 * The wave's boundaries are paired by a half-turn about (50, 60), giving
 * equal crests and troughs, with a 7.5-unit body and 3.6-unit tips.
 * Keep the tips inside the ring:
 * protruding endpoints and extra strokes recreate the old join defects.
 * See docs/design/halcy/2026-09-08-logo-master.md for the approved reference.
 */
export const HALCY_WAVE_PATH =
  'M3.8 58.2C14 51.25 19 47.25 27 47.25C43 47.25 57 65.25 73 65.25C81 65.25 89 61.25 96.2 58.2L96.2 61.8C86 68.75 81 72.75 73 72.75C57 72.75 43 54.75 27 54.75C19 54.75 11 58.75 3.8 61.8Z';

/**
 * The wave redrawn for the reversed favicon, where it sits alone on a solid
 * terracotta disk with no ring.
 *
 * On a disk the lockup wave fails twice: its flat 3.6-unit ends are meant to
 * be buried in the ring band and show as stubs, and a 7.5-unit body with no
 * ring for company is a hairline at 16px. This one is a brush stroke instead:
 * a centreline y = 60 - 9.5 sin(2πt) with a half-width of 6.5 sin(πt), so it
 * is 13 units thick in the middle and tapers continuously to a point at each
 * end. The tips sit exactly on the r=50 perimeter at (1.01, 60) and
 * (98.99, 60), and the two edges are paired by the same half-turn about
 * (50, 60) as HALCY_WAVE_PATH, so the waterline matches the lockup's. Keep it
 * off the ring: a point entering the ring band is exactly the join defect
 * above.
 */
export const HALCY_WAVE_FAVICON_PATH =
  'M1.0102 60C9.1752 53.3241 17.3401 47.1071 25.5051 45.9038C33.6701 44.7005 41.835 48.5258 50 53.5C58.165 58.4742 66.3299 63.7005 74.4949 64.9038C82.6599 66.1071 90.8248 63.2725 98.9898 60C90.8248 66.6759 82.6599 72.8929 74.4949 74.0962C66.3299 75.2995 58.165 71.4742 50 66.5C41.835 61.5258 33.6701 56.2995 25.5051 55.0962C17.3401 53.8929 9.1752 56.7275 1.0102 60Z';

/** Opposite winding leaves the ring open with SVG's default nonzero fill. */
export const HALCY_RING_PATH =
  'M50 0A50 50 0 1 1 50 100A50 50 0 1 1 50 0ZM50 4A46 46 0 1 0 50 96A46 46 0 1 0 50 4Z';

/** Filled silhouette for favicons and Satori, with no stroke to thicken joins. */
export const HALCY_MARK_PATH = `${HALCY_RING_PATH}${HALCY_WAVE_PATH}`;

/**
 * The reversed mark's field and ink: soft white on terracotta, 4.53:1.
 *
 * This pairing is the mark at browser-icon scale: `icon.svg`, `favicon.ico`
 * and the iOS touch icon. It exists as a constant because each of those is a
 * separate file that nothing imports from the others, so the only thing that
 * keeps them one mark is a shared definition. When `apple-icon.png` was a
 * hand-exported binary it sat on the light lockup for weeks after the favicon
 * moved, and nothing caught it: an icon is never looked at again after the PR
 * that made it.
 *
 * Deliberately NOT the LinkedIn company logo, which stays the light lockup --
 * midnight ink on a soft-white field. See that route's own docblock.
 *
 * Do not reuse this pair on the midnight OG field — `#B05A36` measures 2.94:1
 * there, under even the 3:1 non-text floor. `og-brand.ts` explains why those
 * cards carry a lighter amber instead.
 */
export const HALCY_MARK_FIELD = '#B05A36';
export const HALCY_MARK_INK = '#F7F8F6';
